import React, { useState } from 'react';
import { Plus, Trash2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { capCreateRisk, capUpdateRisk, capDeleteRisk } from '../../lib/capstoneApi';
import { inputCls, btnAmber } from './shared';
import GuidePanel from './GuidePanel';

const SEV_STYLES = { High: 'bg-rose-500/15 text-rose-300 border-rose-400/30', Medium: 'bg-amber-400/15 text-amber-300 border-amber-300/30', Low: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30' };
const CATEGORIES = ['Data', 'Timeline', 'Resource', 'Scope', 'Other'];

const parseGuideRisk = (text) => {
  const cat = (text.match(/CATEGORY:\s*([A-Za-z]+)/i) || [])[1];
  const sev = (text.match(/SEVERITY:\s*([A-Za-z]+)/i) || [])[1];
  const mit = (text.split(/Mitigation:/i)[1] || '').trim();
  const desc = text.replace(/CATEGORY:[^|]*\|/i, '').replace(/SEVERITY:[^|]*\|/i, '').split(/Mitigation:/i)[0].trim();
  return { description: desc || text, category: CATEGORIES.includes(cat) ? cat : 'Other', severity: ['High', 'Medium', 'Low'].includes(sev) ? sev : 'Medium', mitigation: mit };
};

export default function RiskTab({ project, risks, reloadRisks }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ description: '', category: 'Other', severity: 'Medium', mitigation: '', owner: '', origin: 'self' });

  const add = async (payload) => {
    try { await capCreateRisk(project.id, payload); reloadRisks(); toast.success('Risk logged'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not log risk'); }
  };

  const setStatus = async (riskId, status) => {
    try { await capUpdateRisk(riskId, { status }); reloadRisks(); } catch { toast.error('Update failed'); }
  };

  return (
    <div data-testid="cap-risks-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="text-[13px] text-white/55">{risks.filter((r) => r.status !== 'Closed').length} open · {risks.length} total</div>
        <button className={btnAmber} onClick={() => setShowAdd((v) => !v)} data-testid="cap-add-risk-btn"><Plus className="w-4 h-4" /> Log risk</button>
      </div>

      <GuidePanel
        topic="risks"
        projectId={project.id}
        label="Let Guide flag likely risks for this project"
        onUse={(text, origin) => add({ ...parseGuideRisk(text), origin })}
      />

      {showAdd && (
        <div className="rounded-xl border border-white/12 bg-slate-900/60 p-4 my-4 space-y-2.5" data-testid="cap-risk-form">
          <input className={inputCls} placeholder="Risk description *" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cap-risk-desc-input" />
          <div className="grid sm:grid-cols-3 gap-2.5">
            <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select className={inputCls} value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
              <option>High</option><option>Medium</option><option>Low</option>
            </select>
            <input className={inputCls} placeholder="Owner" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} />
          </div>
          <input className={inputCls} placeholder="Mitigation plan" value={form.mitigation} onChange={(e) => setForm({ ...form, mitigation: e.target.value })} />
          <button className={btnAmber} disabled={!form.description.trim()} data-testid="cap-risk-save-btn"
            onClick={async () => { await add(form); setForm({ description: '', category: 'Other', severity: 'Medium', mitigation: '', owner: '', origin: 'self' }); setShowAdd(false); }}>
            Save risk
          </button>
        </div>
      )}

      <div className="mt-5 space-y-2.5">
        {risks.length === 0 && (
          <div className="text-center py-10 text-white/35 text-[13px] italic">
            <ShieldAlert className="w-6 h-6 mx-auto mb-2 text-white/20" />
            No risks logged yet — Guide can suggest likely ones for your domain.
          </div>
        )}
        {risks.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-slate-900/50 px-4 py-3" data-testid={`cap-risk-row-${r.id}`}>
            <span className={`px-2 py-0.5 rounded border text-[10px] font-bold ${SEV_STYLES[r.severity] || SEV_STYLES.Medium}`}>{r.severity}</span>
            <span className="text-[10.5px] text-white/40 w-16">{r.category}</span>
            <div className="flex-1 min-w-[200px]">
              <div className="text-[13px] text-white/80">{r.description} {r.origin !== 'self' && <span className="text-amber-200/50">✨</span>}</div>
              {r.mitigation && <div className="text-[11px] text-white/40 mt-0.5 italic">Mitigation: {r.mitigation}</div>}
            </div>
            {r.owner && <span className="text-[10.5px] text-white/40">{r.owner}</span>}
            <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)}
              className="text-[11px] rounded-md px-2 py-1 border border-white/15 bg-transparent text-white/70 focus:outline-none">
              {['Open', 'Mitigating', 'Closed'].map((s) => <option key={s} className="bg-slate-900">{s}</option>)}
            </select>
            <button onClick={() => capDeleteRisk(r.id).then(reloadRisks)} className="text-white/25 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
